import { useEffect, useState } from "react";
import HorizontalAds from "./HorizontalAds";
import AdCreative from "./AdCreative";
import type { BannerAd } from "../../utils/homeData";

type Props = {
  /** Which GLOBAL banner pair: TOP (+ Highlight Global Top Side) or BOTTOM (+ Global Bottom Side). */
  subLocation?: "TOP" | "BOTTOM";
  mock?: string;
  /** Server-seeded GLOBAL banner (order FIRST); see HorizontalAds.initialAd. */
  initialBannerAd?: BannerAd | null;
  /**
   * Server-seeded square that sits beside the banner (GLOBAL/<subLocation>/BANNER/SECOND,
   * see fetchBannerAd). `undefined` fetches both ads on the client instead.
   */
  initialSquareAd?: BannerAd | null;
  /**
   * The page's own content/sidebar split, copied verbatim so the square lines up
   * with the Top Side column underneath. Defaults match the list pages
   * (News, Reviews, Latest, Features, All Guides).
   */
  rowClassName?: string;
  sideClassName?: string;
  /** The page's vertical spacing around the slot (e.g. "my-2", "mb-6"). */
  className?: string;
  /**
   * Breakpoint below which `sideClassName` hides the square: there it is shown
   * under the banner instead, so mobile and tablet get it too. `false` when the
   * side column is always visible.
   */
  stackBelow?: "md" | "lg" | false;
};

const DEFAULT_ROW = "flex gap-10";
const DEFAULT_SIDE = "hidden md:flex w-[30%] justify-center";
// Without a square: the centred 810px banner.
const FALLBACK = "w-full max-w-[810px] mx-auto cursor-pointer empty:hidden";
// Where the square sits beside the banner, the banner fills the row: the 6:5
// square sets the row's height and the banner takes it, so their top and bottom
// edges always meet (on the usual 70/30 split that frame is exactly 4:1).
// Below that breakpoint the square stacks under a 4:1 banner instead.
const FILL_BESIDE: Record<"md" | "lg" | "always", string> = {
  md: "md:absolute md:inset-0 md:aspect-auto",
  lg: "lg:absolute lg:inset-0 lg:aspect-auto",
  always: "absolute inset-0 aspect-auto",
};

const getUrl = (url: string | null) => {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};

const fetchAd = async (subLocation: string, order: "FIRST" | "SECOND"): Promise<BannerAd | null> => {
  try {
    const res = await fetch(
      `${process.env.BACKEND_BASE_URL}/ads?location=GLOBAL&subLocation=${subLocation}&type=BANNER&order=${order}`
    );
    const data = await res.json();
    if (!data.success || !Array.isArray(data.data)) return null;
    return data.data.find((item: any) => Boolean(item?.thumbnail)) ?? null;
  } catch {
    return null;
  }
};

/**
 * A GLOBAL banner (top or bottom), with an optional square ad to its right that
 * aligns with the page's sidebar column. Without a square it renders exactly the old centred
 * banner. The layout depends on whether the square exists, so unseeded pages
 * resolve both ads before rendering anything — otherwise the banner would paint
 * at 810px and then jump into the row.
 */
const BannerRow = ({
  subLocation = "TOP",
  mock,
  initialBannerAd,
  initialSquareAd,
  rowClassName = DEFAULT_ROW,
  sideClassName = DEFAULT_SIDE,
  className = "my-2",
  stackBelow = "md",
}: Props) => {
  const seeded = initialSquareAd !== undefined;
  const [ads, setAds] = useState<{ banner: BannerAd | null | undefined; square: BannerAd | null } | null>(
    seeded ? { banner: initialBannerAd, square: initialSquareAd } : null
  );

  useEffect(() => {
    if (seeded) return;
    let cancelled = false;
    Promise.all([fetchAd(subLocation, "FIRST"), fetchAd(subLocation, "SECOND")]).then(([banner, square]) => {
      if (!cancelled) setAds({ banner, square });
    });
    return () => {
      cancelled = true;
    };
  }, [seeded, subLocation]);

  if (!ads) return null;

  const square = ads.square;
  if (!square?.thumbnail) {
    return (
      <div className={`${FALLBACK} ${className}`}>
        <HorizontalAds type="GLOBAL" subLocation={subLocation} mock={mock} initialAd={ads.banner} />
      </div>
    );
  }

  const banner = (
    <HorizontalAds
      type="GLOBAL"
      subLocation={subLocation}
      mock={mock}
      initialAd={ads.banner}
      fillClassName={FILL_BESIDE[stackBelow === false ? "always" : stackBelow]}
    />
  );

  const squareLink = (
    <AdCreative
      src={square.thumbnail}
      href={getUrl(square.url)}
      title={square.title}
      shape="rect"
      sizes="300px"
      loading="eager"
      className="max-w-[300px] self-start"
    />
  );

  // Same src and sizes as the side copy, so both resolve to one download.
  const stacked =
    stackBelow === "md" ? "flex md:hidden" : stackBelow === "lg" ? "flex lg:hidden" : null;

  return (
    <div className={className}>
      <div className={`${rowClassName} !items-stretch`}>
        <div className="relative w-full min-w-0 cursor-pointer">{banner}</div>
        <div className={sideClassName}>{squareLink}</div>
      </div>
      {stacked ? <div className={`${stacked} justify-center mt-3`}>{squareLink}</div> : null}
    </div>
  );
};

export default BannerRow;
